import {
  Document,
  Page,
  StyleSheet,
  View,
  Text,
  Image,
} from "@react-pdf/renderer";
import { format } from "date-fns";

import { AuthUser } from "../../auth/types/authType";
import { Invoice } from "../types/invoiceTypes";

const styles = StyleSheet.create({
  page: {
    fontSize: 11,
    paddingTop: 0,
    paddingLeft: 30,
    paddingRight: 30,
    paddingBottom: 40,
    lineHeight: 1.5,
    flexDirection: "column",
  },
  header: {
    position: "absolute",
    top: 20,
    left: 0,
    right: 0,
    height: "150px",
    marginBottom: 20,
  },
  body: {
    marginTop: 180,
  },
  title: {
    fontSize: 16,
    textAlign: "center",
    marginBottom: 10,
  },
  metaRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  table: {
    marginTop: 10,
  },
  tableRow: {
    flexDirection: "row",
    borderColor: "#000000",
    borderWidth: 1,
    borderTopWidth: 0,
  },
  tableHeadRow: {
    flexDirection: "row",
    borderColor: "#000000",
    borderWidth: 1,
    backgroundColor: "#eeeeee",
  },
  cellNo: { width: "8%", textAlign: "center", padding: 2 },
  cellName: { width: "42%", padding: 2 },
  cellPassport: { width: "25%", padding: 2 },
  cellAmount: { width: "25%", textAlign: "right", padding: 2 },
  totals: {
    marginTop: 10,
    alignItems: "flex-end",
  },
  totalLine: {
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  totalLabel: { width: "30%", textAlign: "right", paddingRight: 8 },
  totalValue: { width: "20%", textAlign: "right" },
});

interface InvoicePdfProps {
  invoice: Invoice;
  user: AuthUser;
}

export const InvoicePdf = ({ invoice, user }: InvoicePdfProps) => (
  <Document title={invoice.invoiceNumber}>
    <Page size="A4" style={styles.page}>
      <View style={styles.header} fixed>
        <Image
          src={user?.company?.header ? user.company.header : "/noHeader.png"}
        />
      </View>
      <View style={styles.body}>
        <Text style={styles.title}>INVOICE {invoice.invoiceNumber}</Text>
        <View style={styles.metaRow}>
          <Text>To: {invoice.agent?.displayName}</Text>
          <Text>
            Date:{" "}
            {invoice.issueDate &&
              format(new Date(invoice.issueDate), "dd-MMM-yyyy")}
          </Text>
        </View>
        {invoice.remark ? (
          <View style={styles.metaRow}>
            <Text>Remark: {invoice.remark}</Text>
          </View>
        ) : null}

        <View style={styles.table}>
          <View style={styles.tableHeadRow}>
            <Text style={styles.cellNo}>No.</Text>
            <Text style={styles.cellName}>Employee</Text>
            <Text style={styles.cellPassport}>Passport No.</Text>
            <Text style={styles.cellAmount}>Amount (USD)</Text>
          </View>
          {(invoice.lineItems ?? []).map((item, index) => (
            <View style={styles.tableRow} key={index} wrap={false}>
              <Text style={styles.cellNo}>{index + 1}</Text>
              <Text style={styles.cellName}>
                {item.employee?.givenName} {item.employee?.surName}
              </Text>
              <Text style={styles.cellPassport}>
                {item.employee?.passportNumber}
              </Text>
              <Text style={styles.cellAmount}>
                {item.owedAmount?.toLocaleString()}
              </Text>
            </View>
          ))}
        </View>

        <View style={styles.totals}>
          <View style={styles.totalLine}>
            <Text style={styles.totalLabel}>Total:</Text>
            <Text style={styles.totalValue}>
              {invoice.totalOwed?.toLocaleString()} USD
            </Text>
          </View>
          <View style={styles.totalLine}>
            <Text style={styles.totalLabel}>Paid:</Text>
            <Text style={styles.totalValue}>
              {invoice.amountPaid?.toLocaleString()} USD
            </Text>
          </View>
          <View style={styles.totalLine}>
            <Text style={styles.totalLabel}>Balance:</Text>
            <Text style={styles.totalValue}>
              {invoice.balance?.toLocaleString()} USD
            </Text>
          </View>
        </View>
      </View>
    </Page>
  </Document>
);
